import React, { useEffect, useMemo, useState } from 'react';
import { Table, Badge, Card } from 'react-bootstrap';
import PhoenixDocCard from 'components/base/PhoenixDocCard';
import { useSelector } from 'react-redux';
import { selectProjectResults } from '../data-access/project.reducer';
import { pickLatestGovernanceRun } from '../../shared/governanceResultsAccess';
import GovernanceTabLoadingState from '../shared/GovernanceTabLoadingState';

// Shape persisted by POST /project/features/internal-policy-compliance
// under collection_results.results.internal_policy_compliance (one entry per run).
interface PolicyViolationRecord {
  policy_id?: string;
  policy_name?: string;
  document_name?: string;
  rule_id?: string;
  rule_title?: string;
  violated_by?: string; // PROMPT | OUTPUT | BOTH
  violation_type?: string; // commission | omission
  severity?: string; // LOW | MEDIUM | HIGH | CRITICAL
  action?: string; // ALLOW | WARN | FLAG | MASK | BLOCK | REVIEW
  recommended_response?: string;
  evidence?: {
    matched_prompt_text?: string;
    matched_output_text?: string;
    source_excerpt?: string | null;
    chunk_number?: number | null;
    similarity?: number | null;
    document_name?: string;
  };
  detection_layer?: number; // 1 | 2 | 3
  confidence?: number;
  reasoning?: string;
  status?: string; // VIOLATION | REVIEW
  item_id?: string;
  processing_index?: number;
}

interface PolicyViolationEntry {
  item_id?: string;
  processing_index?: number;
  status?: string;
  violations?: PolicyViolationRecord[];
}

interface PolicyViolationRun {
  result_id?: string;
  title?: string;
  timestamp?: string | { $date?: string };
  overall_status?: string; // PASS | WARNING | HIGH_RISK | BLOCK | NO_POLICY_CONFIGURED
  pairs_evaluated?: number;
  engine?: { tier?: string; rulebook_tokens?: number; models?: Record<string, string> };
  entries?: PolicyViolationEntry[];
  chart_data?: {
    summary?: {
      pairs_evaluated?: number;
      total_violations?: number;
      review_count?: number;
      critical?: number;
      high?: number;
      medium?: number;
      low?: number;
    };
  };
  warnings?: string[];
}

interface PolicyViolationPanelProps {
  isLoading?: boolean;
  loadingLabel?: string;
}

const toTime = (ts: PolicyViolationRun['timestamp']): number => {
  if (!ts) return 0;
  const raw = typeof ts === 'object' ? ts.$date : ts;
  const parsed = raw ? Date.parse(raw) : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const PolicyViolationPanel = ({ isLoading = false, loadingLabel }: PolicyViolationPanelProps) => {
  const projectResults = useSelector(selectProjectResults);
  const [data, setData] = useState<PolicyViolationRun | null>(null);

  // Latest run from results.internal_policy_compliance (array, newest by timestamp).
  const latestRun = useMemo<PolicyViolationRun | null>(
    () => pickLatestGovernanceRun<PolicyViolationRun>(projectResults, 'internal_policy_compliance'),
    [projectResults]
  );

  useEffect(() => {
    setData(latestRun ?? null);
  }, [latestRun]);

  const getSeverityBadge = (severity?: string) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL':
        return <Badge bg="dark">CRITICAL</Badge>;
      case 'HIGH':
        return <Badge bg="danger">HIGH</Badge>;
      case 'MEDIUM':
        return (
          <Badge bg="warning" text="dark">
            MEDIUM
          </Badge>
        );
      case 'LOW':
        return <Badge bg="info">LOW</Badge>;
      default:
        return <Badge bg="secondary">{severity || 'N/A'}</Badge>;
    }
  };

  const getOverallBadge = (status?: string) => {
    switch (status?.toUpperCase()) {
      case 'PASS':
        return <Badge bg="success">PASS</Badge>;
      case 'WARNING':
        return (
          <Badge bg="warning" text="dark">
            WARNING
          </Badge>
        );
      case 'HIGH_RISK':
        return <Badge bg="danger">HIGH RISK</Badge>;
      case 'BLOCK':
        return <Badge bg="dark">BLOCK</Badge>;
      case 'NO_POLICY_CONFIGURED':
        return <Badge bg="secondary">NO POLICY CONFIGURED</Badge>;
      default:
        return <Badge bg="secondary">{status || 'UNKNOWN'}</Badge>;
    }
  };

  const getStatusBadge = (status?: string) =>
    status === 'REVIEW' ? (
      <Badge bg="warning" text="dark">
        REVIEW
      </Badge>
    ) : (
      <Badge bg="danger">VIOLATION</Badge>
    );

  // Flatten every violation across evaluated pairs for the table.
  const violations: PolicyViolationRecord[] = useMemo(
    () => (data?.entries || []).flatMap(entry => entry.violations || []),
    [data]
  );
  const summary = data?.chart_data?.summary;

  const evidenceText = (v: PolicyViolationRecord): string => {
    const e = v.evidence || {};
    if (v.violated_by === 'PROMPT') return e.matched_prompt_text || '';
    return e.matched_output_text || e.matched_prompt_text || '';
  };

  return (
    <div className="mb-4">
      <PhoenixDocCard className="mb-4">
        <PhoenixDocCard.Header title="Policy Violation Analysis">
          <p className="text-muted mb-0">
            Prompt / output pairs evaluated against the organization&apos;s canonical AI policies by the MICROeAI
            Adaptive Policy Evaluation Engine (APEE).
          </p>
        </PhoenixDocCard.Header>
        <PhoenixDocCard.Body>
          {isLoading && !data && <GovernanceTabLoadingState label={loadingLabel} />}

          {data && (
            <div className="mt-4">
              <div className="d-flex align-items-center flex-wrap gap-4 mb-4 p-3 bg-light rounded border">
                <div>
                  <h5 className="mb-1">Overall Posture</h5>
                  {getOverallBadge(data.overall_status)}
                </div>
                {data.title && (
                  <div>
                    <h5 className="mb-1">Title</h5>
                    <span className="fw-bold text-primary">{data.title}</span>
                  </div>
                )}
                {typeof data.pairs_evaluated === 'number' && (
                  <div>
                    <h5 className="mb-1">Pairs Evaluated</h5>
                    <span className="text-muted">{data.pairs_evaluated}</span>
                  </div>
                )}
                {data.engine?.tier && (
                  <div>
                    <h5 className="mb-1">Engine</h5>
                    <span className="text-muted">
                      Tier {data.engine.tier}
                      {data.engine.rulebook_tokens ? ` · ${data.engine.rulebook_tokens} tokens` : ''}
                    </span>
                  </div>
                )}
                {data.timestamp && (
                  <div>
                    <h5 className="mb-1">Generated At</h5>
                    <span className="text-muted">{new Date(toTime(data.timestamp)).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {summary && (
                <div className="d-flex flex-wrap gap-3 mb-4">
                  {[
                    { label: 'Total Violations', value: summary.total_violations, bg: 'primary' },
                    { label: 'Critical', value: summary.critical, bg: 'dark' },
                    { label: 'High', value: summary.high, bg: 'danger' },
                    { label: 'Medium', value: summary.medium, bg: 'warning' },
                    { label: 'Low', value: summary.low, bg: 'info' },
                    { label: 'Needs Review', value: summary.review_count, bg: 'secondary' }
                  ].map(s => (
                    <div key={s.label} className="text-center px-3 py-2 border rounded bg-white">
                      <div className="fs-5 fw-bold">{s.value ?? 0}</div>
                      <div className="small text-muted">{s.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {(data.warnings?.length ?? 0) > 0 && (
                <div className="alert alert-warning py-2 small" role="alert">
                  {data.warnings!.map((w, i) => (
                    <div key={i}>{w}</div>
                  ))}
                </div>
              )}

              {violations.length > 0 ? (
                <>
                  <h5 className="mb-3">Policy Violations</h5>
                  <div className="table-responsive">
                    <Table bordered hover className="align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '18%' }}>Policy / Document</th>
                          <th style={{ width: '16%' }}>Rule</th>
                          <th style={{ width: '9%' }}>Severity</th>
                          <th style={{ width: '8%' }}>Action</th>
                          <th style={{ width: '9%' }}>Violated By</th>
                          <th style={{ width: '9%' }}>Type</th>
                          <th style={{ width: '7%' }}>Layer</th>
                          <th style={{ width: '24%' }}>Evidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {violations.map((v, idx) => (
                          <tr key={idx}>
                            <td>
                              <div className="fw-semibold small">{v.policy_name || v.policy_id}</div>
                              <div className="text-muted small">{v.document_name}</div>
                            </td>
                            <td>
                              <div className="fw-semibold small">{v.rule_title}</div>
                              <div className="text-muted small">{v.rule_id}</div>
                            </td>
                            <td>{getSeverityBadge(v.severity)}</td>
                            <td>
                              <Badge bg="light" text="dark" className="border">
                                {v.action || 'N/A'}
                              </Badge>
                            </td>
                            <td>
                              <span className="small">{v.violated_by}</span>
                              <div className="mt-1">{getStatusBadge(v.status)}</div>
                            </td>
                            <td>
                              <span className="small text-capitalize">{v.violation_type}</span>
                            </td>
                            <td>
                              <Badge bg="secondary" className="small">
                                L{v.detection_layer}
                              </Badge>
                              {typeof v.confidence === 'number' && (
                                <div className="text-muted small mt-1">{(v.confidence * 100).toFixed(0)}%</div>
                              )}
                            </td>
                            <td>
                              <small className="text-muted d-block">
                                {evidenceText(v)?.substring(0, 140)}
                                {(evidenceText(v)?.length ?? 0) > 140 && '…'}
                              </small>
                              {v.evidence?.source_excerpt && (
                                <div className="mt-1">
                                  <Badge bg="light" text="dark" className="border small">
                                    Source
                                    {typeof v.evidence.similarity === 'number'
                                      ? ` · ${(v.evidence.similarity * 100).toFixed(0)}%`
                                      : ''}
                                  </Badge>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {violations.some(v => v.reasoning || v.recommended_response) && (
                    <div className="mt-4">
                      <h5 className="mb-3">Reasoning &amp; Recommended Responses</h5>
                      <Card className="bg-light border-0">
                        <Card.Body>
                          {violations.map(
                            (v, idx) =>
                              (v.reasoning || v.recommended_response) && (
                                <div key={idx} className="mb-3">
                                  <div className="fw-bold mb-1">
                                    {v.rule_title} <span className="text-muted small">({v.rule_id})</span>
                                  </div>
                                  {v.reasoning && <div className="small text-muted">{v.reasoning}</div>}
                                  {v.recommended_response && (
                                    <div className="small mt-1">
                                      <span className="fw-semibold">Recommended: </span>
                                      {v.recommended_response}
                                    </div>
                                  )}
                                </div>
                              )
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  )}
                </>
              ) : (
                <div className="alert alert-success py-3" role="alert">
                  <span className="fw-semibold">No policy violations detected.</span>{' '}
                  {data.overall_status === 'NO_POLICY_CONFIGURED'
                    ? 'No active policies are configured for this project.'
                    : 'All evaluated prompt/output pairs are compliant with the configured policies.'}
                </div>
              )}
            </div>
          )}

          {!isLoading && !data && (
            <div className="text-center py-5 text-muted">
              <h5>No Analysis Data</h5>
              <p>No Policy Violation analysis data available for this project.</p>
            </div>
          )}
        </PhoenixDocCard.Body>
      </PhoenixDocCard>
    </div>
  );
};

export default PolicyViolationPanel;
